import type { CSSProperties } from 'react';

import type { BubbleShape } from '@/lib/engage/showroom-form-schema';
import { WIDGET_TEXT_PRIMARY } from '@/lib/engage/widget-typography';

const DEFAULT_CORNER_RADIUS = 12;
const DEFAULT_LAUNCHER_RADIUS = 24;

/** Tailwind radius class for chat bubbles (rounded shape only). */
export function resolveBubbleClassName(
  bubbleShape: BubbleShape | undefined,
  cornerRadius: number | undefined,
): string {
  const shape = bubbleShape ?? 'rounded';
  const radius = cornerRadius ?? DEFAULT_CORNER_RADIUS;
  if (shape === 'pill') return 'rounded-full';
  if (radius <= 4) return 'rounded-sm';
  if (radius >= 20) return 'rounded-2xl';
  return 'rounded-xl';
}

export type BubbleColorStyle = {
  backgroundColor: string;
  color?: string;
};

/** Inline bubble colors + optional speech-tail radius for rounded shape. */
export function resolveBubbleInlineStyle(
  isUser: boolean,
  bubbleShape: BubbleShape | undefined,
  colors: BubbleColorStyle,
): CSSProperties {
  const shape = bubbleShape ?? 'rounded';
  if (shape !== 'rounded') {
    return colors;
  }
  return isUser
    ? { ...colors, borderBottomRightRadius: '4px' }
    : { ...colors, borderBottomLeftRadius: '4px' };
}

/** Outer panel shell border radius from Branding corner radius. */
export function resolvePanelBorderRadius(cornerRadius: number | undefined): string {
  if (typeof cornerRadius === 'number' && !Number.isNaN(cornerRadius)) {
    return `${cornerRadius}px`;
  }
  return `${DEFAULT_CORNER_RADIUS}px`;
}

/** Header top radii align with panel shell. */
export function resolveHeaderBorderRadius(cornerRadius: number | undefined): CSSProperties {
  const radius = resolvePanelBorderRadius(cornerRadius);
  return {
    borderTopLeftRadius: radius,
    borderTopRightRadius: radius,
  };
}

export function resolveHeaderBackground(
  userBubbleColor: string,
  headerGradient: boolean | undefined,
): string {
  if (headerGradient === false) {
    return userBubbleColor;
  }
  return `linear-gradient(135deg, ${userBubbleColor} 0%, ${userBubbleColor}dd 100%)`;
}

const LAUNCHER_BUTTON_BASE_CLASS =
  `inline-flex h-14 min-h-[56px] min-w-[56px] items-center justify-center px-5 ${WIDGET_TEXT_PRIMARY} shadow-lg shadow-zinc-900/20 transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] dark:shadow-black/40`;

/** Launcher button classes — omit fixed radius/text color when theme supplies them. */
export function resolveLauncherButtonClassName(options: {
  launcherRadius?: number;
  launcherTextColor?: string;
}): string {
  const parts = [LAUNCHER_BUTTON_BASE_CLASS];
  if (typeof options.launcherRadius !== 'number' || Number.isNaN(options.launcherRadius)) {
    parts.push('rounded-full');
  }
  if (!options.launcherTextColor?.trim()) {
    parts.push('text-white');
  }
  return parts.join(' ');
}

export function resolveLauncherButtonStyle(options: {
  launcherFillColor: string;
  launcherTextColor?: string;
  launcherRadius?: number;
}): CSSProperties {
  const style: CSSProperties = {
    backgroundColor: options.launcherFillColor,
  };
  const textColor = options.launcherTextColor?.trim();
  if (textColor) {
    style.color = textColor;
  } else {
    style.color = '#ffffff';
  }
  const radius =
    typeof options.launcherRadius === 'number' && !Number.isNaN(options.launcherRadius)
      ? options.launcherRadius
      : DEFAULT_LAUNCHER_RADIUS;
  style.borderRadius = `${radius}px`;
  return style;
}
